/** What the overview should tell you to do next.
 *
 *  Derived, not stored: every item is a fact already in the breakdown,
 *  phrased as work. Kept pure so the THRESHOLDS are testable — the thing
 *  that decides whether this list is useful or cries wolf.
 *
 *  Ordered by severity then by size, so the list ranks itself. That
 *  ordering is the whole reason this is a list rather than a grid of
 *  equal cards: position carries priority.
 */

export type ActionSeverity = "critical" | "warning" | "note";

export type ActionItem = {
  key: string;
  severity: ActionSeverity;
  /** The headline number, already the thing being counted. */
  count: number;
  /** Share of its own denominator, for the magnitude bar (0–100). */
  magnitude: number;
  /** Route to act on it, or null when the overview can only report. */
  to: string | null;
};

export type ActionInput = {
  campaignId: string;
  total: number;
  neverInvited: number;
  noShow: number;
  conform: number;
  nonConform: number;
  conformityPending: number;
  sessions?: {
    created: number;
    closed: number;
    total: number;
    /** Invited, waiting on the beneficiary to pick a slot. Not work. */
    awaiting_booking?: number;
    /** Slot came and went unused. This IS work. */
    overdue?: number;
  };
};

/** Investigated 2026-09-27. The first version of this rule counted every
 *  `created` session and called 489 of them a backlog. 467 of those were
 *  blank booking shells — `create_blank_booking` opens one with no
 *  `scheduled_for` the moment the wave invites someone, so they are the
 *  normal state of "invited, yet to choose a slot", not undone work.
 *  Nothing was overdue at all.
 *
 *  That is precisely the cry-wolf failure thresholds are meant to
 *  prevent, and it happened because the METRIC was wrong rather than the
 *  threshold. Stuck work is now counted directly: a session whose slot
 *  has passed, unstarted. One is one too many, so there is no ratio. */

/** Under this many judged inspections, a failure rate is noise — one
 *  non-conform out of two is 50% and means nothing. */
export const MIN_JUDGED_FOR_RATE = 10;

/** A failure rate at or above this is worth surfacing on the overview
 *  rather than leaving in the report. */
export const HIGH_NON_CONFORM_PCT = 50;

function pct(n: number, of: number): number {
  return of > 0 ? Math.round((n / of) * 100) : 0;
}

export function campaignActions(input: ActionInput): ActionItem[] {
  const {
    campaignId,
    total,
    neverInvited,
    noShow,
    conform,
    nonConform,
    conformityPending,
    sessions,
  } = input;
  const items: ActionItem[] = [];

  const sessionTotal = sessions?.total ?? 0;
  const overdue = sessions?.overdue ?? 0;
  if (overdue > 0) {
    items.push({
      key: "overdue",
      severity: "critical",
      count: overdue,
      magnitude: pct(overdue, sessionTotal),
      // The campaign's OWN sessions tab, not `/sessions`. That top-level
      // page is hardcoded `scope="standalone"` — it lists sessions with
      // no campaign at all and ignores `?campaign=`, so this link used to
      // answer "which of MY inspections are overdue" with somebody
      // else's list entirely.
      to: `/campaigns/${campaignId}?tab=sessions`,
    });
  }

  const judged = conform + nonConform;
  const failPct = pct(nonConform, judged);
  if (judged >= MIN_JUDGED_FOR_RATE && failPct >= HIGH_NON_CONFORM_PCT) {
    items.push({
      key: "nonConformRate",
      severity: "critical",
      count: failPct,
      magnitude: failPct,
      to: null,
    });
  }

  if (neverInvited > 0) {
    items.push({
      key: "neverInvited",
      severity: "warning",
      count: neverInvited,
      magnitude: pct(neverInvited, total),
      to: null,
    });
  }

  if (noShow > 0) {
    items.push({
      key: "noShow",
      severity: "warning",
      count: noShow,
      magnitude: pct(noShow, total),
      to: `/campaigns/${campaignId}?tab=beneficiaries`,
    });
  }

  if (conformityPending > 0) {
    items.push({
      key: "unjudged",
      severity: "note",
      count: conformityPending,
      magnitude: pct(conformityPending, sessionTotal),
      to: null,
    });
  }

  const rank: Record<ActionSeverity, number> = {
    critical: 0,
    warning: 1,
    note: 2,
  };
  return items.sort(
    (a, b) => rank[a.severity] - rank[b.severity] || b.magnitude - a.magnitude,
  );
}
